import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { canAccessDashboard } from '../auth/roles';
import { APP_VERSION } from '../version';
import {
  readReadyCounts, readPendingLists, markReady, PENDING_EVENT,
} from '../utils/pendingReports';
import { fetchReportV5, fetchReportV5Detailed, REPORT_STATUS } from '../api/reports';

// 后台轮询间隔：用户离开等待页之后由顶导接手监控；5s 在"用户感知"和"请求量"之间折中。
const BG_POLL_INTERVAL_MS = 5000;

export default function PageLayout({ children, showNav = true, showFooter = true, authPage = false }) {
  const { user, logout, sso } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState(readReadyCounts);

  // 监听 sessionStorage 变化（同标签由 markPending/markReady/markSeen/clearAll 触发自定义事件；
  // 跨标签由 storage 事件触发）。
  useEffect(() => {
    const refresh = () => setCounts(readReadyCounts());
    window.addEventListener(PENDING_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(PENDING_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  // 全局后台 polling：仅登录态启用；每 5s 检查 pending.* 里每个 id 是否已 done，
  // done 就 markReady（顶导亮气泡）。无 pending 时单次 tick 是 noop，不发请求。
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const { rough, detailed } = readPendingLists();
      if (rough.length === 0 && detailed.length === 0) return;
      await Promise.all([
        ...rough.map(id => fetchReportV5(id).then(res => {
          if (!cancelled && res?.status === REPORT_STATUS.DONE) markReady('rough', id);
        }).catch(() => {})),
        ...detailed.map(id => fetchReportV5Detailed(id).then(res => {
          if (!cancelled && res?.status === REPORT_STATUS.DONE) markReady('detailed', id);
        }).catch(() => {})),
      ]);
    };
    tick();
    const timer = setInterval(tick, BG_POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [user]);

  const handleLogout = async () => { await logout(); if (!sso.enabled) navigate('/'); };

  return (
    // QA 2026-04-29 批次一 F2：根容器加 isolation:isolate，确保 sticky header
    // 的 zIndex:100 形成独立堆叠上下文——之前在 Chromium 全页面截图模式下，
    // 内部 section 的 transform/blend 容器偶发把 header "吃"到下一层，
    // 表现为 header 渲染在 hero 中部（QA 报告 ISSUE-001/002）。
    <div className={authPage ? 'unified-page' : undefined} style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', isolation: 'isolate' }}>
      {showNav && (
        <header style={{
          background: '#fff',
          borderBottom: '1px solid #d6e0f5',  /* v5-line */
          padding: '0 40px', height: 64, display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
          boxShadow: '0 1px 0 rgba(15, 35, 80, 0.04)',
          position: 'sticky', top: 0, zIndex: 100,
          flexShrink: 0, alignSelf: 'stretch',
        }}>
          <Link to="/" style={{
            display: 'inline-flex', alignItems: 'center', gap: 9,
            textDecoration: 'none',
            fontWeight: 800, fontSize: 17, color: '#0e1730',  /* v5-ink */
            letterSpacing: '0.01em',
          }}>
            <span style={{
              width: 28, height: 28,
              background: 'linear-gradient(135deg, #1f53e6, #0d2b8a)',
              color: '#fff',
              borderRadius: 8,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'Consolas, "SF Mono", "Cascadia Mono", monospace',
              fontSize: 13, fontWeight: 800,
            }}>Q</span>
            前哨科技
            <span style={{
              color: '#6a7596',
              fontFamily: 'Consolas, "SF Mono", "Cascadia Mono", monospace',
              fontWeight: 500, letterSpacing: '0.04em', fontSize: 11.5,
              marginLeft: 4,
            }}>QianShao.AI</span>
          </Link>
          <nav style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
            {user ? (
              <>
                {canAccessDashboard(user) && (
                  <Link to="/dashboard" style={{ color: '#26314f', fontSize: 13.5, textDecoration: 'none' }}>企业看板</Link>
                )}
                <Link to="/history" style={{
                  color: '#26314f', fontSize: 13.5, textDecoration: 'none',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                  历史记录
                  {counts.rough > 0 && (
                    <span title="粗略报告新生成" style={{
                      background: '#e5484d', color: '#fff',
                      fontSize: 11, fontWeight: 700,
                      padding: '1px 6px', borderRadius: 10, minWidth: 16,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      lineHeight: 1.2,
                    }}>{counts.rough}</span>
                  )}
                  {counts.detailed > 0 && (
                    <span title="详细报告新生成" style={{
                      background: '#1f53e6', color: '#fff',
                      fontSize: 11, fontWeight: 700,
                      padding: '1px 6px', borderRadius: 10, minWidth: 16,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      lineHeight: 1.2,
                    }}>{counts.detailed}</span>
                  )}
                </Link>
                <Link to="/profile" style={{ color: '#26314f', fontSize: 13.5, textDecoration: 'none' }}>
                  {user.display_name}
                </Link>
                <button onClick={handleLogout} style={{
                  background: 'none', border: '1.5px solid #d6e0f5',
                  borderRadius: 8, padding: '7px 16px',
                  color: '#26314f', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                }}>退出</button>
              </>
            ) : (
              <>
                <Link to="/login" style={{ color: '#26314f', fontSize: 13.5, textDecoration: 'none' }}>登录</Link>
                <Link to="/register" style={{
                  background: '#4f46e5', color: '#fff',
                  padding: '8px 20px', borderRadius: 8,
                  fontSize: 13, fontWeight: 600, textDecoration: 'none',
                }}>注册</Link>
              </>
            )}
          </nav>
        </header>
      )}
      <main style={{ flex: 1 }}>{children}</main>
      {showFooter && (
        <footer style={{
          background: '#fff',
          borderTop: '1px solid #d6e0f5',
          padding: '20px 40px',
          textAlign: 'center',
          color: '#6a7596',
          fontSize: 12,
          lineHeight: 1.7,
          fontFamily: 'Consolas, "SF Mono", monospace',
        }}>
          <div>© 2026 前哨科技 QianShao.AI · AI 领导力测评系统</div>
          {!authPage && <div style={{ marginTop: 4, opacity: 0.75 }}>
            代码版本 v{APP_VERSION}
          </div>}
        </footer>
      )}
    </div>
  );
}

export function CenteredLayout({ children, maxWidth = 480 }) {
  return (
    <PageLayout>
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
        padding: '60px 20px',
      }}>
        <div style={{ width: '100%', maxWidth }}>{children}</div>
      </div>
    </PageLayout>
  );
}
